import React from 'react';
import { useStore } from 'zustand';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useUnityBuildStore } from '../../stores/store';
import { useDragManager } from './DragManager';

interface SVGCursorPreviewProps {
  cursorPosition: { x: number; y: number };
}

/**
 * Shows a preview of the current assembly's shape at the cursor position
 * with high transparency to help users place elements accurately
 */
export const SVGCursorPreview: React.FC<SVGCursorPreviewProps> = ({ cursorPosition }) => {
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const {
    isAnnotatingTakeoff,
    isDragging,
    isInsertingPoint,
    isEditingPointFillColor,
    isEditingPointBorderColor,
    isEditingLineColor,
  } = useStore(useUnityBuildStore);
  const { isDragging: isDraggingPoints } = useDragManager();

  // Only show preview when we have a current assembly and are annotating,
  // but not when dragging, inserting, or editing colors
  if (
    !currentTakeoffAssembly ||
    !isAnnotatingTakeoff ||
    isDragging ||
    isDraggingPoints ||
    isInsertingPoint ||
    isEditingPointFillColor ||
    isEditingPointBorderColor ||
    isEditingLineColor
  ) {
    return null;
  }

  const pointSize = currentTakeoffAssembly.pointsize || 5;
  const pointType = currentTakeoffAssembly.pointtype || 'circle';
  const fillColor = currentTakeoffAssembly.pointfillcolor || 'red';
  const strokeColor = currentTakeoffAssembly.pointbordercolor || 'black';

  // High transparency for preview
  const previewProps = {
    fill: fillColor,
    stroke: strokeColor,
    fillOpacity: 0.15, // Very transparent
    strokeOpacity: 0.3, // Slightly more visible stroke
    strokeWidth: 1,
    pointerEvents: 'none' as const, // Don't interfere with mouse events
  };

  const renderPreviewShape = () => {
    switch (pointType) {
      case 'circle':
        return (
          <circle cx={cursorPosition.x} cy={cursorPosition.y} r={pointSize} {...previewProps} />
        );

      case 'square':
      case 'rect':
        return (
          <rect
            x={cursorPosition.x - pointSize / 2}
            y={cursorPosition.y - pointSize / 2}
            width={pointSize}
            height={pointSize}
            {...previewProps}
          />
        );

      case 'triangle':
        const trianglePoints = `
          ${cursorPosition.x},${cursorPosition.y - pointSize / 2}
          ${cursorPosition.x - pointSize / 2},${cursorPosition.y + pointSize / 2}
          ${cursorPosition.x + pointSize / 2},${cursorPosition.y + pointSize / 2}
        `;
        return <polygon points={trianglePoints} {...previewProps} />;

      case 'diamond':
        const diamondPoints = `
          ${cursorPosition.x},${cursorPosition.y - pointSize / 2}
          ${cursorPosition.x + pointSize / 2},${cursorPosition.y}
          ${cursorPosition.x},${cursorPosition.y + pointSize / 2}
          ${cursorPosition.x - pointSize / 2},${cursorPosition.y}
        `;
        return <polygon points={diamondPoints} {...previewProps} />;

      case 'cross':
        const crossStrokeWidth = Math.max(1, Math.round(pointSize / 6));
        return (
          <g {...previewProps}>
            <line
              x1={cursorPosition.x - pointSize / 2}
              y1={cursorPosition.y - pointSize / 2}
              x2={cursorPosition.x + pointSize / 2}
              y2={cursorPosition.y + pointSize / 2}
              stroke={strokeColor}
              strokeWidth={crossStrokeWidth}
              strokeOpacity={0.3}
            />
            <line
              x1={cursorPosition.x - pointSize / 2}
              y1={cursorPosition.y + pointSize / 2}
              x2={cursorPosition.x + pointSize / 2}
              y2={cursorPosition.y - pointSize / 2}
              stroke={strokeColor}
              strokeWidth={crossStrokeWidth}
              strokeOpacity={0.3}
            />
          </g>
        );

      case 'plus':
        const plusStrokeWidth = Math.max(1, Math.round(pointSize / 6));
        return (
          <g {...previewProps}>
            <line
              x1={cursorPosition.x - pointSize / 2}
              y1={cursorPosition.y}
              x2={cursorPosition.x + pointSize / 2}
              y2={cursorPosition.y}
              stroke={strokeColor}
              strokeWidth={plusStrokeWidth}
              strokeOpacity={0.3}
            />
            <line
              x1={cursorPosition.x}
              y1={cursorPosition.y - pointSize / 2}
              x2={cursorPosition.x}
              y2={cursorPosition.y + pointSize / 2}
              stroke={strokeColor}
              strokeWidth={plusStrokeWidth}
              strokeOpacity={0.3}
            />
          </g>
        );

      case 'star':
        const rOuter = pointSize / 2;
        const rInner = rOuter / 2.5;
        const starPoints: string[] = [];
        for (let i = 0; i < 10; i++) {
          const angle = (Math.PI / 5) * i - Math.PI / 2;
          const r = i % 2 === 0 ? rOuter : rInner;
          starPoints.push(
            `${cursorPosition.x + r * Math.cos(angle)},${cursorPosition.y + r * Math.sin(angle)}`
          );
        }
        return <polygon points={starPoints.join(' ')} {...previewProps} />;

      case 'hexagon':
        const hexRadius = pointSize / 2;
        const hexPoints: string[] = [];
        for (let i = 0; i < 6; i++) {
          const angle = (Math.PI / 3) * i - Math.PI / 2;
          hexPoints.push(
            `${cursorPosition.x + hexRadius * Math.cos(angle)},${cursorPosition.y + hexRadius * Math.sin(angle)}`
          );
        }
        return <polygon points={hexPoints.join(' ')} {...previewProps} />;

      default:
        return (
          <circle cx={cursorPosition.x} cy={cursorPosition.y} r={pointSize} {...previewProps} />
        );
    }
  };

  return <g className="cursor-preview">{renderPreviewShape()}</g>;
};
